import type { EnemyDefinition } from '../../model/enemies'
import type { EnemyDefinitionV2 } from './types'
import { resolveEnemyStats } from './resolveEnemyStats'

const NONE_ARMOR = { armorType: 'NONE' as const, armorValue: 0 }
const LIGHT_ARMOR = {
  armorType: 'LIGHT' as const,
  armorValue: 12,
  damageModifiers: { PIERCE: 1.15, EXPLOSIVE: 0.95, ENERGY: 1 } as const
}
const HEAVY_ARMOR = {
  armorType: 'HEAVY' as const,
  armorValue: 40,
  damageModifiers: { EXPLOSIVE: 1.2, PIERCE: 0.9, COLD: 1.05, ENERGY: 1 } as const
}
const REINFORCED_ARMOR = {
  armorType: 'REINFORCED' as const,
  armorValue: 28,
  damageModifiers: { ARCANE: 1.2, TOXIC: 0.85, ENERGY: 1, PIERCE: 0.95 } as const
}

/** ACTIVE enemy catalog — V2 source of truth. Lv1 NORMAL/BOSS ≡ M41. */
export const ENEMY_DEFINITIONS_V2: Record<string, EnemyDefinitionV2> = {
  normal: {
    id: 'normal',
    displayName: 'Normal',
    movementClass: 'GROUND',
    defaultEnemyRank: 'NORMAL',
    baseStats: {
      maxHp: 100,
      speed: 3,
      leakDamage: 1,
      reward: 10,
      radius: 0.375,
      color: 0xef4444
    },
    armorProfile: { ...NONE_ARMOR },
    traits: {},
    rewardProfile: { killReward: 10 },
    visualId: 'normal'
  },
  fast: {
    id: 'fast',
    displayName: 'Fast',
    movementClass: 'GROUND',
    defaultEnemyRank: 'NORMAL',
    baseStats: {
      maxHp: 60,
      speed: 5,
      leakDamage: 1,
      reward: 8,
      radius: 0.3,
      color: 0xfbbf24
    },
    armorProfile: { ...NONE_ARMOR },
    traits: {},
    rewardProfile: { killReward: 8 },
    visualId: 'fast'
  },
  tank: {
    id: 'tank',
    displayName: 'Tank',
    movementClass: 'GROUND',
    defaultEnemyRank: 'NORMAL',
    baseStats: {
      maxHp: 350,
      speed: 1.6,
      leakDamage: 3,
      reward: 25,
      radius: 0.48,
      color: 0x7c3aed
    },
    armorProfile: { ...HEAVY_ARMOR },
    traits: {},
    rewardProfile: { killReward: 25 },
    visualId: 'tank'
  },
  regen: {
    id: 'regen',
    displayName: 'Regen',
    movementClass: 'GROUND',
    defaultEnemyRank: 'NORMAL',
    baseStats: {
      maxHp: 180,
      speed: 2.4,
      leakDamage: 2,
      reward: 18,
      radius: 0.4,
      color: 0x14b8a6
    },
    armorProfile: { ...LIGHT_ARMOR },
    traits: { regen: { hpPerSecond: 6, delayAfterDamage: 2 } },
    rewardProfile: { killReward: 18 },
    visualId: 'regen'
  },
  shield: {
    id: 'shield',
    displayName: 'Shield',
    movementClass: 'GROUND',
    defaultEnemyRank: 'NORMAL',
    baseStats: {
      maxHp: 140,
      speed: 2.2,
      leakDamage: 2,
      reward: 20,
      radius: 0.42,
      color: 0x64748b
    },
    armorProfile: { ...REINFORCED_ARMOR },
    traits: { shield: { maxShield: 120 } },
    rewardProfile: { killReward: 20 },
    visualId: 'shield'
  },
  boss: {
    id: 'boss',
    displayName: 'Boss',
    movementClass: 'GROUND',
    defaultEnemyRank: 'BOSS',
    baseStats: {
      maxHp: 1800,
      speed: 1.15,
      leakDamage: 10,
      reward: 150,
      radius: 0.62,
      color: 0x7f1d1d
    },
    armorProfile: { ...HEAVY_ARMOR, armorValue: 50 },
    traits: { statusDurationMultiplier: 0.7 },
    rewardProfile: { killReward: 150 },
    visualId: 'boss'
  },
  /**
   * AIR family — fragile/fast relative to ground tanks; distinct from FAST (ground).
   * Forces BOTH-target coverage along airRoute; Cannon cannot engage.
   */
  flyer: {
    id: 'flyer',
    displayName: 'Flyer',
    movementClass: 'AIR',
    defaultEnemyRank: 'NORMAL',
    baseStats: {
      maxHp: 36,
      speed: 3.1,
      leakDamage: 1,
      reward: 12,
      radius: 0.32,
      color: 0x38bdf8
    },
    armorProfile: { ...LIGHT_ARMOR, armorValue: 8 },
    traits: {},
    rewardProfile: { killReward: 12 },
    visualId: 'flyer'
  },
  /**
   * Dedicated AIR boss family (not Flyer+BOSS rank) — different silhouette & threat curve.
   */
  air_boss: {
    id: 'air_boss',
    displayName: 'Air Boss',
    movementClass: 'AIR',
    defaultEnemyRank: 'BOSS',
    baseStats: {
      maxHp: 520,
      speed: 1.45,
      leakDamage: 6,
      reward: 90,
      radius: 0.55,
      color: 0x0369a1
    },
    armorProfile: { ...HEAVY_ARMOR, armorValue: 35 },
    traits: {
      immuneStatuses: ['poison'],
      statusDurationMultiplier: 0.75
    },
    rewardProfile: { killReward: 90 },
    visualId: 'air_boss'
  },
  /**
   * Guardian — heavily armored ground. Mage/Armor Break creates the tactical answer.
   * Moderate HP; armor is the threat (not a second Boss).
   */
  guardian: {
    id: 'guardian',
    displayName: 'Guardian',
    movementClass: 'GROUND',
    defaultEnemyRank: 'NORMAL',
    baseStats: {
      maxHp: 260,
      speed: 1.35,
      leakDamage: 3,
      reward: 28,
      radius: 0.5,
      color: 0x94a3b8
    },
    armorProfile: {
      armorType: 'REINFORCED',
      armorValue: 55,
      damageModifiers: { ARCANE: 1.25, EXPLOSIVE: 1.1, TOXIC: 0.9, PIERCE: 0.85, ENERGY: 1 }
    },
    traits: {},
    rewardProfile: { killReward: 28 },
    visualId: 'guardian'
  },
  /**
   * Phase — mid-speed ground with CONTROL duration resistance (not immunity).
   * Frost remains useful at ~half duration; Deep Freeze similarly shortened.
   */
  phase: {
    id: 'phase',
    displayName: 'Phase',
    movementClass: 'GROUND',
    defaultEnemyRank: 'NORMAL',
    baseStats: {
      maxHp: 120,
      speed: 3.4,
      leakDamage: 2,
      reward: 16,
      radius: 0.36,
      color: 0xa78bfa
    },
    armorProfile: { ...LIGHT_ARMOR, armorValue: 10 },
    traits: {
      statusCategoryDurationMultipliers: { CONTROL: 0.5 }
    },
    rewardProfile: { killReward: 16 },
    visualId: 'phase'
  },
  /**
   * Crystal — geometric TOXIC-resistant ground. Poison less optimal; Arcane favored.
   * Uses damage resistance (not poison immunity — Air Boss already owns that niche).
   */
  crystal: {
    id: 'crystal',
    displayName: 'Crystal',
    movementClass: 'GROUND',
    defaultEnemyRank: 'NORMAL',
    baseStats: {
      maxHp: 160,
      speed: 2.1,
      leakDamage: 2,
      reward: 19,
      radius: 0.4,
      color: 0x67e8f9
    },
    armorProfile: {
      armorType: 'LIGHT',
      armorValue: 15,
      damageModifiers: { TOXIC: 0.5, ARCANE: 1.25, ENERGY: 1, COLD: 1.05 }
    },
    traits: {
      statusCategoryDurationMultipliers: { DOT: 0.65 }
    },
    rewardProfile: { killReward: 19 },
    visualId: 'crystal'
  }
}

/**
 * Materialize legacy EnemyDefinition from V2 at Lv1 + default rank.
 * Used so GameSession / catalogs stay numeric-compatible with M41.
 */
export function materializeEnemyDefinition(def: EnemyDefinitionV2): EnemyDefinition {
  const resolved = resolveEnemyStats(def, {
    enemyLevel: 1,
    enemyRank: def.defaultEnemyRank
  })
  return {
    id: def.id,
    name: def.displayName,
    maxHealth: resolved.maxHp,
    speed: resolved.speed,
    reward: resolved.reward,
    leakDamage: resolved.leakDamage,
    radius: resolved.radius,
    color: resolved.color,
    regen: resolved.regen,
    shield: resolved.shield,
    movementClass: resolved.movementClass,
    defaultEnemyRank: def.defaultEnemyRank,
    armorProfile: resolved.armorProfile,
    damageModifiers: resolved.damageModifiers,
    immuneStatuses: resolved.immuneStatuses,
    immuneStatusCategories: resolved.immuneStatusCategories,
    statusDurationMultiplier: resolved.statusDurationMultiplier,
    statusCategoryDurationMultipliers: resolved.statusCategoryDurationMultipliers,
    visualId: resolved.visualId
  }
}
